import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Bell, Mail, MessageSquare, ShieldCheck } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * User-controlled routing for OTP + security alerts.
 * - Default: email (keeps Twilio spend at $0 for cost-conscious deployments).
 * - Users can opt into SMS only when they have a phone on file.
 * - Operational notifications (job assignments, weekly reports) always stay
 *   push+email — this panel only controls the security channel.
 */
export default function NotificationPreferences() {
  const [prefs, setPrefs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const token = localStorage.getItem('session_token') || '';
  const authHeaders = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };

  const load = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/me/notification-preferences`, {
        headers: authHeaders,
      });
      const data = await res.json();
      if (res.ok) setPrefs(data);
    } catch (e) {
      console.error('[notif prefs]', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const update = async (payload) => {
    setSaving(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/auth/me/notification-preferences`, {
        method: 'PATCH',
        headers: authHeaders,
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (!res.ok) {
        toast.error(data.detail || 'Could not update preferences');
        return;
      }
      toast.success('Preferences updated');
      setPrefs((p) => ({ ...p, ...payload }));
    } catch (e) {
      console.error('[notif prefs]', e);
      toast.error('Could not update preferences');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return null;
  if (!prefs) return null;

  const method = prefs.preferred_auth_method || 'email';
  const secChannel = prefs.notify_security_channel || method;
  const canUseSms = !!prefs.phone_on_file;

  return (
    <div
      className="rounded-lg border border-white/10 bg-black/40 p-5 space-y-4"
      data-testid="notification-preferences-card"
    >
      <div className="flex items-start gap-2">
        <Bell className="h-5 w-5 text-[#FFD60A] mt-0.5" />
        <div>
          <h3 className="text-white font-semibold" style={{ fontFamily: 'Barlow Condensed', letterSpacing: '0.04em' }}>
            NOTIFICATION PREFERENCES
          </h3>
          <p className="text-xs text-white/60 mt-0.5">
            Security codes (OTP for password reset + change) and new-device alerts. Operational
            notifications (assignments, reports) always come via push + email.
          </p>
        </div>
      </div>

      {!canUseSms && (
        <div className="text-xs bg-amber-950/30 border border-amber-700/40 rounded px-3 py-2 text-amber-200" data-testid="notif-prefs-no-phone-note">
          Add a phone number to your profile to enable SMS-based security codes.
        </div>
      )}

      <ChoiceRow
        label="Verification codes (OTP)"
        helper="How password-reset and change-password codes are delivered."
        icon={ShieldCheck}
        value={method}
        onChange={(v) => update({ preferred_auth_method: v })}
        canUseSms={canUseSms}
        saving={saving}
        testidPrefix="notif-prefs-otp"
      />

      <ChoiceRow
        label="Security alerts (new device sign-in)"
        helper="Where we send alerts when a new browser / phone signs into your account."
        icon={Bell}
        value={secChannel}
        onChange={(v) => update({ notify_security_channel: v })}
        canUseSms={canUseSms}
        saving={saving}
        testidPrefix="notif-prefs-security"
      />
    </div>
  );
}

function ChoiceRow({ label, helper, icon: Icon, value, onChange, canUseSms, saving, testidPrefix }) {
  return (
    <div>
      <div className="flex items-center gap-2 text-sm text-white">
        <Icon className="h-4 w-4 text-[#C9A962]" /> <span className="font-medium">{label}</span>
      </div>
      <p className="text-[11px] text-white/50 ml-6 mb-2">{helper}</p>
      <div className="flex gap-2 ml-6" data-testid={`${testidPrefix}-toggle`}>
        <button
          type="button"
          data-testid={`${testidPrefix}-email`}
          onClick={() => onChange('email')}
          disabled={saving}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition ${
            value === 'email'
              ? 'bg-[#FFD60A] text-black border-transparent font-semibold'
              : 'bg-transparent text-white border-white/20 hover:bg-white/5'
          }`}
        >
          <Mail className="h-3 w-3" /> Email
        </button>
        <button
          type="button"
          data-testid={`${testidPrefix}-sms`}
          onClick={() => onChange('sms')}
          disabled={saving || !canUseSms}
          className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs border transition ${
            value === 'sms'
              ? 'bg-[#FFD60A] text-black border-transparent font-semibold'
              : `bg-transparent text-white border-white/20 hover:bg-white/5 ${!canUseSms ? 'opacity-40 cursor-not-allowed' : ''}`
          }`}
        >
          <MessageSquare className="h-3 w-3" /> SMS
        </button>
      </div>
    </div>
  );
}
